import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Zap, 
  Calendar,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Table,
  DollarSign,
  Activity,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { NativeButton, NativeSelect } from '../../components/NativeComponents';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');

export default function ResidentChargingHistoryPage() {
  const { user, isSimulationActive, stopRoleSimulation } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState({ total_cost: 0, total_energy: 0, session_count: 0 });
  const [allTime, setAllTime] = useState({ total_cost: 0, total_energy: 0, session_count: 0 });
  const [downloading, setDownloading] = useState(null);
  
  // Ay/Yıl filtresi
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  
  // Simülasyonu bitir
  const handleStopSimulation = () => {
    if (stopRoleSimulation) {
      stopRoleSimulation();
      navigate('/dashboard');
    }
  };

  const fetchData = useCallback(async () => {
    if (simulationActive) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/resident/charging-history?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        setSummary(data.summary || { total_cost: 0, total_energy: 0, session_count: 0 });
        setAllTime(data.all_time || { total_cost: 0, total_energy: 0, session_count: 0 });
      }
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, simulationActive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPDF = async () => {
    setDownloading('pdf');
    try {
      const response = await fetch(
        `${API_URL}/api/resident/charging-report/pdf?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sarj-raporu-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}.pdf`;
        a.click();
        toast.success('PDF indirildi');
      } else {
        toast.error('PDF indirilemedi');
      }
    } catch (err) {
      toast.error('PDF indirme hatası');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading('excel');
    try {
      const response = await fetch(
        `${API_URL}/api/resident/charging-report/excel?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sarj-raporu-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}.xlsx`;
        a.click();
        toast.success('Excel indirildi');
      } else {
        toast.error('Excel indirilemedi');
      }
    } catch (err) {
      toast.error('Excel indirme hatası');
    } finally {
      setDownloading(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const diff = Math.floor((new Date(end) - new Date(start)) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
  };

  const months = [
    { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' }, { value: 3, label: 'Mart' },
    { value: 4, label: 'Nisan' }, { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' }, { value: 9, label: 'Eylül' },
    { value: 10, label: 'Ekim' }, { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' }
  ];

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
    years.push(y);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Simülasyon modunda uyarı göster
  if (simulationActive) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        {/* Simülasyon Banner */}
        <div className="bg-red-500 text-white px-4 py-2 rounded-lg mb-4 flex items-center justify-between">
          <span>SİMÜLASYON MODU: Site Sakini görünümü</span>
          <button onClick={handleStopSimulation} className="bg-white text-red-500 px-3 py-1 rounded text-sm font-medium">
            Simülasyonu Bitir
          </button>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Simülasyon Modunda Görüntüleme
          </h2>
          <p className="text-yellow-700 dark:text-yellow-300">
            Gerçek kullanıcı verileri görüntülenemiyor. Bu sayfa Site Sakini rolünün göreceği ekranı simüle ediyor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-4xl mx-auto" data-testid="resident-charging-history-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Şarj Geçmişim</h1>
          <p className="text-muted-foreground text-sm">Şarj oturumlarınız ve harcamalarınız</p>
        </div>
        
        {/* Filtre ve İndirme */}
        <div className="flex items-center gap-2 flex-wrap">
          <NativeSelect
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="w-28"
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </NativeSelect>
          
          <NativeSelect
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="w-24"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </NativeSelect>
          
          <NativeButton
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            disabled={downloading === 'pdf'}
          >
            {downloading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            PDF
          </NativeButton>
          
          <NativeButton
            variant="outline"
            size="sm"
            onClick={handleDownloadExcel}
            disabled={downloading === 'excel'}
          >
            {downloading === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Table className="w-4 h-4 mr-1" />}
            Excel
          </NativeButton>
        </div>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Bu Ay Harcama</p>
              <p className="text-xl font-bold text-foreground">{summary.total_cost?.toLocaleString('tr-TR')} ₺</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Bu Ay Enerji</p>
              <p className="text-xl font-bold text-foreground">{summary.total_energy?.toLocaleString('tr-TR')} kWh</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Bu Ay Oturum</p>
              <p className="text-xl font-bold text-foreground">{summary.session_count}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Toplam Harcama</p>
              <p className="text-xl font-bold text-foreground">{allTime.total_cost?.toLocaleString('tr-TR')} ₺</p>
            </div>
          </div>
        </div>
      </div>

      {/* Oturum Listesi */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Şarj Oturumları ({months.find(m => m.value === selectedMonth)?.label} {selectedYear})
          </h2>
        </div>
        
        {sessions.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Bu dönemde şarj oturumu bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tarih</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">İstasyon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Süre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Enerji</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tutar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sessions.map((session, idx) => (
                  <tr key={session.id || idx} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground">
                      {formatDateTime(session.start_time)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {session.station_name || session.ocpp_id || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDuration(session.start_time, session.end_time)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {((session.energy_consumed || 0) / 1000).toFixed(2)} kWh
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {(session.total_cost || 0).toFixed(2)} ₺
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
